import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../services/api';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const statusColor = (s) => {
  switch (String(s || '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'paused':
      return 'default';
    default:
      return 'info';
  }
};

/**
 * Franchisee / Mitra web — published Learn Zone courses + peer referrals.
 * Locsy tracks status only — publisher settles via own UPI.
 */
export default function LearnZonePeerReferrals() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [myCourses, setMyCourses] = useState([]);
  const [available, setAvailable] = useState([]);
  const [mySales, setMySales] = useState(null);
  const [asPublisher, setAsPublisher] = useState(null);
  const [disclaimers, setDisclaimers] = useState(null);
  const [salesRows, setSalesRows] = useState([]);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareCourse, setShareCourse] = useState(null);
  const [sharePhone, setSharePhone] = useState('');
  const [shareLink, setShareLink] = useState(null);
  const [acceptShare, setAcceptShare] = useState(false);
  const [busy, setBusy] = useState(false);

  const [settleOpen, setSettleOpen] = useState(false);
  const [settleRow, setSettleRow] = useState(null);
  const [settleNote, setSettleNote] = useState('');
  const [settleUtr, setSettleUtr] = useState('');
  const [acceptSettle, setAcceptSettle] = useState(false);

  const [refOpen, setRefOpen] = useState(false);
  const [refCourse, setRefCourse] = useState(null);
  const [refEnabled, setRefEnabled] = useState(false);
  const [refRate, setRefRate] = useState('10');
  const [acceptRef, setAcceptRef] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [coursesRes, refRes, salesRes] = await Promise.all([
        api.get('/local-learn/me/courses'),
        api.get('/local-learn/me/peer-referrals'),
        api.get('/local-learn/me/course-sales'),
      ]);
      setMyCourses(Array.isArray(coursesRes.data?.data) ? coursesRes.data.data : []);
      const data = refRes.data?.data || {};
      setAvailable(Array.isArray(data.available) ? data.available : []);
      setMySales(data.mySales || null);
      setAsPublisher(data.asPublisher || null);
      setDisclaimers(data.disclaimers || null);
      setSalesRows(Array.isArray(salesRes.data?.data?.items) ? salesRes.data.data.items : []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openShare = (course) => {
    setShareCourse(course);
    setSharePhone('');
    setShareLink(null);
    setAcceptShare(false);
    setShareOpen(true);
  };

  const createAndShare = async () => {
    if (!shareCourse || !acceptShare) return;
    setBusy(true);
    try {
      const res = await api.post('/local-learn/me/peer-referrals/links', {
        courseCode: shareCourse.courseCode,
        acceptTerms: true,
        publisherBusinessId: shareCourse.publisherBusinessId || undefined,
      });
      const link = res.data?.data;
      setShareLink(link);
      const phone = String(sharePhone || '').replace(/\D/g, '').slice(-10);
      const text = link?.whatsappText || link?.shareUrl || '';
      const wa =
        phone.length === 10
          ? `https://wa.me/91${phone}?text=${encodeURIComponent(text)}`
          : `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(wa, '_blank', 'noopener,noreferrer');
      if (link?.id) {
        try {
          await api.post(`/local-learn/me/peer-referrals/links/${link.id}/shared`, {});
        } catch (_) {
          /* soft */
        }
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Share failed');
    } finally {
      setBusy(false);
    }
  };

  const openSettle = (row) => {
    setSettleRow(row);
    setSettleNote('');
    setSettleUtr('');
    setAcceptSettle(false);
    setSettleOpen(true);
  };

  const doSettle = async () => {
    if (!settleRow || !acceptSettle) return;
    setBusy(true);
    try {
      await api.post(
        `/local-learn/me/peer-referrals/commissions/${settleRow.id}/settle`,
        {
          acceptDisclaimer: true,
          note: settleNote,
          upiRef: settleUtr,
        },
      );
      setSettleOpen(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Settle failed');
    } finally {
      setBusy(false);
    }
  };

  const openReferralSettings = (course) => {
    setRefCourse(course);
    setRefEnabled(course.peerReferralEnabled === true);
    setRefRate(String(course.peerReferralRatePercent ?? 10));
    setAcceptRef(false);
    setRefOpen(true);
  };

  const saveReferralSettings = async () => {
    if (!refCourse || !acceptRef) return;
    setBusy(true);
    try {
      await api.put(
        `/local-learn/me/courses/${encodeURIComponent(refCourse.courseCode)}/peer-referral`,
        {
          enabled: refEnabled,
          ratePercent: Number(refRate) || 0,
          acceptTerms: true,
        },
      );
      setRefOpen(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to save referral %');
    } finally {
      setBusy(false);
    }
  };

  const setVisibility = async (course, action) => {
    const labels = {
      hide: 'Hide this course from Citizen Learn Zone?',
      show: 'Show this course again in Citizen Learn Zone?',
      delete: 'Archive (soft delete) this course? Sales history is kept; it will not appear in catalog.',
    };
    if (!window.confirm(labels[action] || 'Continue?')) return;
    setBusy(true);
    try {
      await api.post(
        `/local-learn/me/courses/${encodeURIComponent(course.courseCode)}/visibility`,
        { action },
      );
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Visibility update failed');
    } finally {
      setBusy(false);
    }
  };

  const summaryChips = (block) => {
    const s = block?.summary || {};
    return (
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip label={`Owed ${money(s.owedRupees)} (${s.owedCount || 0})`} color="warning" />
        <Chip label={`Settled ${money(s.settledRupees)} (${s.settledCount || 0})`} color="success" />
      </Stack>
    );
  };

  const commissionTable = (block, asPublisher) => {
    const items = Array.isArray(block?.items) ? block.items : [];
    if (!items.length) {
      return <Typography color="text.secondary">No referral sales yet.</Typography>;
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Course</TableCell>
            <TableCell>Commission</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Status</TableCell>
            {asPublisher ? <TableCell align="right">Action</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id}>
              <TableCell>
                {c.courseName}
                <Typography variant="caption" display="block" color="text.secondary">
                  {c.ratePercent}% of {money(c.grossAmountRupees)}
                </Typography>
              </TableCell>
              <TableCell>{money(c.commissionRupees)}</TableCell>
              <TableCell>{c.citizenPhone}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={String(c.status || '').toUpperCase()}
                  color={c.status === 'settled' ? 'success' : 'warning'}
                />
              </TableCell>
              {asPublisher ? (
                <TableCell align="right">
                  {c.status === 'owed' ? (
                    <Button size="small" variant="contained" onClick={() => openSettle(c)}>
                      Settle
                    </Button>
                  ) : (
                    '—'
                  )}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Course Referrals
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>My courses</strong>: courses you published (Learn Zone) — plus App Admin LMS
        catalog if your login has LMS features. Hotels without LMS see only their own Learn Zone courses.
        <br />
        <strong>Available</strong>: App Admin published courses from Course Management + other
        LMS Vyapars&apos; courses with referral on — promote via WhatsApp. Students see the same
        published catalog in the student app.
      </Alert>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="My courses" />
        <Tab label="Available to promote" />
        <Tab label="My sales" />
        <Tab label="I owe (publisher)" />
        <Tab label="My course txns" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={1.5}>
          <Alert severity="warning" sx={{ mb: 0 }}>
            {disclaimers?.publisher?.en ||
              'When you enable referral %, you owe promoters after paid enrollments. Pay from your own UPI, then mark Settled under “I owe”.'}
          </Alert>
          {!myCourses.length ? (
            <Typography color="text.secondary">
              No courses here. LMS / coaching centers: publish under Learn Zone or enable LMS to
              manage Course Management courses. Hotels: use the Available tab to promote App Admin
              / other Vyapar courses.
            </Typography>
          ) : (
            myCourses.map((c) => {
              const isLms = c.catalogSource === 'lms';
              const status = String(c.marketplaceStatus || 'draft');
              const visible =
                (isLms && c.isActive !== false) ||
                (status === 'approved' && c.publishToApp === true && c.isActive !== false);
              const archived = String(c.rejectionReason || '') === 'owner_archived';
              return (
                <Card key={`${c.catalogSource}-${c.courseCode}`} variant="outlined">
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      spacing={1}
                      alignItems={{ sm: 'flex-start' }}
                    >
                      <Box>
                        <Typography fontWeight={800}>{c.courseName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {c.courseCode}
                          {` · Fee ${money(c.onlineFee || c.fee)}`}
                          {c.brandLabel ? ` · ${c.brandLabel}` : ''}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            color={isLms ? 'info' : statusColor(status)}
                            label={isLms ? 'LMS / COURSE MGMT' : status.toUpperCase()}
                          />
                          {visible ? (
                            <Chip size="small" color="success" label="PUBLISHED" />
                          ) : (
                            <Chip size="small" label={archived ? 'ARCHIVED' : 'INACTIVE'} />
                          )}
                          {c.peerReferralEnabled ? (
                            <Chip
                              size="small"
                              color="primary"
                              label={`Referral ${c.peerReferralRatePercent ?? 0}%`}
                            />
                          ) : (
                            <Chip size="small" label="Referral OFF" />
                          )}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busy || archived}
                          onClick={() => openReferralSettings(c)}
                        >
                          Referral %
                        </Button>
                        {c.canManageVisibility && visible ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() => setVisibility(c, 'hide')}
                          >
                            Hide
                          </Button>
                        ) : null}
                        {c.canManageVisibility &&
                        (status === 'paused' || (status === 'approved' && !visible)) &&
                        !archived ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={busy}
                            onClick={() => setVisibility(c, 'show')}
                          >
                            Display
                          </Button>
                        ) : null}
                        {c.canManageVisibility && !archived ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={busy}
                            onClick={() => setVisibility(c, 'delete')}
                          >
                            Delete
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={1.5}>
          <Alert severity="info">
            {disclaimers?.referrer?.en ||
              'Other Vyapars’ courses with peer referral enabled. Commission is paid by the publisher, not Locsy.'}
          </Alert>
          {!available.length ? (
            <Typography color="text.secondary">
              No promotable courses yet. Publish courses under Course Management
              (publish to app), or wait for LMS Vyapars to enable referral % on Learn Zone courses.
            </Typography>
          ) : (
            available.map((c) => (
              <Card key={c.courseCode} variant="outlined">
                <CardContent>
                  <Typography fontWeight={800}>{c.courseName}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Fee {money(c.onlineFee)} · ~{c.ratePercent}% (~{money(c.estimatedCommissionRupees)}) after paid enroll — paid by publisher, not Locsy
                  </Typography>
                  <Button variant="contained" onClick={() => openShare(c)}>
                    WhatsApp referral link
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      )}

      {tab === 2 && (
        <Box>
          {summaryChips(mySales)}
          {commissionTable(mySales, false)}
        </Box>
      )}

      {tab === 3 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {disclaimers?.publisher?.en ||
              'Pay promoters from your own UPI, then mark Settled. Locsy does not transfer funds or mediate disputes.'}
          </Alert>
          {summaryChips(asPublisher)}
          {commissionTable(asPublisher, true)}
        </Box>
      )}

      {tab === 4 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Online enrollments for courses you published. Referral rows show commission status.
          </Alert>
          {!salesRows.length ? (
            <Typography color="text.secondary">No online enrollments yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Referral</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesRows.map((r) => (
                  <TableRow key={r.enrollmentId}>
                    <TableCell>{r.courseName}</TableCell>
                    <TableCell>{money(r.amountRupees)}</TableCell>
                    <TableCell>{r.citizenPhone}</TableCell>
                    <TableCell>{String(r.paymentStatus || '').toUpperCase()}</TableCell>
                    <TableCell>
                      {r.hasReferral
                        ? `${money(r.commissionRupees)} · ${r.commissionStatus}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      <Dialog open={refOpen} onClose={() => setRefOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Peer referral %</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {refCourse?.courseName}
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={refEnabled} onChange={(e) => setRefEnabled(e.target.checked)} />
            }
            label="Enable peer referrals"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Commission % (max 50)"
            type="number"
            value={refRate}
            onChange={(e) => setRefRate(e.target.value)}
            inputProps={{ min: 0, max: 50, step: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox checked={acceptRef} onChange={(e) => setAcceptRef(e.target.checked)} />
            }
            label="I settle promoters from my own UPI; Locsy does not guarantee or mediate"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!acceptRef || busy}
            onClick={saveReferralSettings}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>WhatsApp referral link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {shareCourse?.courseName}
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            label="Customer WhatsApp (10 digits)"
            value={sharePhone}
            onChange={(e) => setSharePhone(e.target.value)}
            inputProps={{ maxLength: 10 }}
          />
          {shareLink?.shareUrl ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {shareLink.shareUrl}
            </Typography>
          ) : null}
          <FormControlLabel
            control={
              <Checkbox checked={acceptShare} onChange={(e) => setAcceptShare(e.target.checked)} />
            }
            label="I understand Locsy does not pay or guarantee referral commissions"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!acceptShare || busy}
            onClick={createAndShare}
          >
            Open WhatsApp
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={settleOpen} onClose={() => setSettleOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Mark commission settled</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Pay {money(settleRow?.commissionRupees)} to promoter UPI{' '}
            {settleRow?.referrerUpiSnapshot || '(ask promoter)'} from your own UPI, then mark settled.
            Locsy does not transfer money.
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            label="UTR / txn id (optional)"
            value={settleUtr}
            onChange={(e) => setSettleUtr(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Note (optional)"
            value={settleNote}
            onChange={(e) => setSettleNote(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox checked={acceptSettle} onChange={(e) => setAcceptSettle(e.target.checked)} />
            }
            label={
              disclaimers?.settle?.en ||
              'I attest I paid outside Locsy; Locsy does not verify the UPI transfer'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!acceptSettle || busy}
            onClick={doSettle}
          >
            Mark settled
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
